import { autoinject } from "aurelia-framework";

@autoinject
export class DialogClientService {

    private _stack: { view: any, model: any, _canHide: (boolean | (() => boolean)), multiLayer: boolean, showCloseButton: boolean, fullscreen: boolean, classList: string, scrollEntireDialog: boolean, visible?: boolean }[] = [];
    private _showFuncs: { func: ((view: any, model: any, showCloseButton: boolean, fullscreen: boolean, classList: string, multiLayer: boolean, scrollEntireDialog: boolean, visible?: boolean) => Promise<void> | void) }[] = []
    private _hideFuncs: { func: ((view: any, model: any, removeFromStack?: boolean) => Promise<void> | void) }[] = []

    public get isModalVisible() {
        return this._stack.length > 0;
    }

    //Get the view of the last dialog
    public get view() {
        return this._stack.length > 0 ? this._stack[this._stack.length - 1].view : null;
    }

    //Get the model of the last dialog
    public get model() {
        return this._stack.length > 0 ? this._stack[this._stack.length - 1].model : null;
    }

    //Subscribe to show functions
    public subscribeShow(funcShow: (view: any, model: any, showCloseButton: boolean, fullscreen: boolean, classList: string, scrollEntireDialog: boolean, multiLayer: boolean) => Promise<void> | void) {
        this._showFuncs.push({ func: funcShow });
    }

    //Unsubscribe from show functions
    public unSubscribeShow(showFunc: (view, viewModel, showCloseButton, fullscreen, classList: string, scrollEntireDialog: boolean, multiLayer: boolean) => Promise<void> | void) {
        const func = this._showFuncs.filter(x => x.func == showFunc)[0];
        const index = this._showFuncs.indexOf(func);
        if (index > -1) {
            this._showFuncs.splice(index, 1);
        }
    }

    //Subscribe to hide functions
    public subscribeHide(funcHide: (view: any, model: any) => Promise<void> | void) {
        this.unSubscribeHide(funcHide);
        this._hideFuncs.push({ func: funcHide });
    }

    //Unsubscribe from hide functions
    public unSubscribeHide(funcHide: (view: any, model: any) => Promise<void> | void) {
        const func = this._hideFuncs.filter(x => x.func == funcHide)[0];
        const index = this._hideFuncs.indexOf(func);
        if (index > -1) {
            this._hideFuncs.splice(index, 1);
        }
    }

    //Show dialog
    public async show(view: any, model: any, canHide: (boolean | (() => boolean)) = true, showCloseButton: boolean = false, fullscreen: boolean = false, classList: string = "", scrollEntireDialog: boolean = false, multiLayer: boolean = true): Promise<void> {
        if (this.isModalVisible && this.view == view) {
            this.hide(true);
        }

        this._stack.push({ view: view, model: model, multiLayer: multiLayer, _canHide: canHide, showCloseButton, fullscreen, classList, scrollEntireDialog, visible: true });

        if (!multiLayer) {
            for (let i = 0; i < this._stack.length - 1; i++) {
                const previousDialog = this._stack[i];
                if (previousDialog.visible) {
                    previousDialog.visible = false;

                    // Call hide functions but indicate dialog is not being removed from stack
                    for (const func of this._hideFuncs.map(x => x.func)) {
                        const promise = func(previousDialog.view, previousDialog.model, false);
                        if (promise instanceof Promise) {
                            await promise;
                        }
                    }
                }
            }
        }

        for (const func of this._showFuncs.map(x => x.func)) {
            const promise = func(view, model, showCloseButton, fullscreen, classList, scrollEntireDialog, multiLayer);
            if (promise instanceof Promise) {
                await promise;
            }
        }
    }

    //Hide the last dialog
    public async hide(forceHide: boolean = true, closeAll: boolean = false) {
        const func = async () => {
            let lastItem = this._stack[this._stack.length - 1];
            if (((typeof lastItem._canHide == "function" && !lastItem._canHide()) || !lastItem._canHide) && !forceHide) {
                return;
            }

            //Remove the last dialog
            lastItem = this._stack.pop();
            if (!lastItem) {
                return;
            }

            //Execute hide functions for the removed dialog
            const hideFuncs = this._hideFuncs.map(x => x.func);
            for (let index = hideFuncs.length - 1; index >= 0; index--) {
                const func = hideFuncs[index];
                const promise = func(lastItem.view, lastItem.model, true);
                if (promise instanceof Promise) {
                    await promise;
                }
            }
            
            //Make previous dialog visible if it exists
            if (this._stack.length > 0) {
                const previousDialog = this._stack[this._stack.length - 1];
                if (!previousDialog.visible) {
                    previousDialog.visible = true;
                    
                    // Show the previously hidden dialog
                    for (const func of this._showFuncs.map(x => x.func)) {
                        const promise = func(
                            previousDialog.view, 
                            previousDialog.model, 
                            previousDialog.showCloseButton, 
                            previousDialog.fullscreen, 
                            previousDialog.classList,
                            previousDialog.multiLayer,
                            previousDialog.scrollEntireDialog,
                            true
                        );
                        if (promise instanceof Promise) {
                            await promise;
                        }
                    }
                }
            }

            //Blur
            this.blur();
        };

        if (this._stack.length > 0) {
            if (closeAll) {
                while (this._stack.length > 0) {
                    await func();
                }
            } else {
                await func();
            }
        }
    }

    //Blur the active element - This is used to hide the keyboard on mobile devices
    private blur() {
        const activeElement: any = document.activeElement;
        if (activeElement && activeElement.blur) {
            activeElement.blur();
        }
    }
}